import React, { useState } from "react";
import { useRoute } from "@react-navigation/native";
import { RootRouteProps } from "router/types/stack";

import { StyleSheet, TextInput, TouchableOpacity, View } from "react-native";

import {
  Icon,
  Empty,
  Error,
  Header,
  Loading,
  InfinityList,
  ProductCard,
  Text,
  Separator,
} from "component";
import FooterScreenCartSummary from "screen/shared/FooterScreenCartSummary";

import { colors, sizes, strings } from "asset/constant";
import { css } from "style";
import { liveSearchInput, mq, sn } from "utils";

import { useProducts } from "client/api";
import { useRecoilValue } from "recoil";
import { cartAtom } from "store";
import { useFlag } from "hook";
import fonts from "asset/fonts";
import tinycolor from "tinycolor2";

let timer: ReturnType<typeof setTimeout>;

export default function Products() {
  const cart = useRecoilValue(cartAtom);

  const params = useRoute<RootRouteProps<"products">>().params;

  const { flg: typing, on: startTyping, off: stopTyping } = useFlag();
  const [title, setTitle] = useState("");

  const [discount, setDiscount] = useState<0 | 1>(0);
  const addDiscountFilter = () => setDiscount(1);
  const removeDiscountFilter = () => setDiscount(0);

  const {
    data,
    error,
    isError,
    refetch,
    isLoading,
    hasNextPage,
    isRefetching,
    fetchNextPage,
    isFetchingNextPage,
  } = useProducts({ id: params.id, title, discount });

  const _renderItem = (items: []) => {
    return (
      <View style={{ ...css.wrap, ...css.rRowCenterAlignSpaceBetween }}>
        {items?.map((product: ProductT, key: number) => (
          <View key={String(key)} style={css.responsiveBoxContainer}>
            <ProductCard key={String(key)} product={product} />
          </View>
        ))}
      </View>
    );
  };

  const _renderContent = () => {
    if (isLoading || typing) {
      return (
        <>
          <Loading style={{ top: mq(-50) }} />
          <View style={{ ...css.container }} />
        </>
      );
    }

    if (isError) {
      return <Error error={error} reset={refetch} loading={isRefetching} />;
    }

    if (data?.pages?.[0]?.data?.data?.length === 0) {
      return (
        <Empty
          style={{ minHeight: mq(100) }}
          message={strings.not_any_item_to_show}
        />
      );
    }

    return (
      <InfinityList
        onRefresh={refetch}
        renderItemList={_renderItem}
        responseData={data}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        paddingBottom={mq(70)}
      />
    );
  };

  return (
    <>
      <Header title={strings.products} subTitle={params.title} />
      <View style={{ ...css.container }}>
        <View style={{ paddingVertical: mq(5) }}>
          <View style={{ paddingHorizontal: sizes.space }}>
            <View
              style={{
                ...css.fitWidth,
                overflow: "hidden",
                marginTop: 5,
                height: sn(48, 48, 43, 43),
                borderWidth: mq(1),
                borderColor: colors.border.normal,
                borderRadius: sizes.radius.lg,
                ...css.rRowCenterAlign,
              }}
            >
              <View style={{ ...css.square(45), ...css.centralElements }}>
                <Icon name="search1" size={mq(25)} />
              </View>
              <TextInput
                placeholderTextColor={colors.text.placeholder}
                placeholder={strings.insert_title_search}
                style={{
                  ...css.container,
                  ...css.textAlignR,
                  backgroundColor: colors.white,
                  fontFamily: fonts.fa.default,
                  color: colors.text.normal,
                  height: sn(46, 46, 41, 41),
                }}
                onChangeText={(text) =>
                  liveSearchInput(
                    String(text),
                    title,
                    setTitle,
                    timer,
                    startTyping,
                    stopTyping
                  )
                }
              />
            </View>

            <View style={styles.tabs}>
              <TouchableOpacity
                onPress={addDiscountFilter}
                style={{
                  ...styles.tab,
                  backgroundColor:
                    discount === 1
                      ? tinycolor(colors.theme).lighten(60).toString()
                      : colors.transparent,
                }}
              >
                <Text
                  size={"lg"}
                  weight={discount === 1 ? "bold" : "default"}
                  color={discount === 1 ? colors.theme : colors.text.normal}
                >
                  {strings.discounted}
                </Text>
              </TouchableOpacity>
              <Separator sidesSpace={1} axis="y" color={colors.white} />
              <TouchableOpacity
                onPress={removeDiscountFilter}
                style={{
                  ...styles.tab,
                  backgroundColor:
                    discount === 0
                      ? tinycolor(colors.theme).lighten(60).toString()
                      : colors.transparent,
                }}
              >
                <Text
                  size={"lg"}
                  weight={discount === 0 ? "bold" : "default"}
                  color={discount === 0 ? colors.theme : colors.text.normal}
                >
                  {strings.all}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
        {_renderContent()}
      </View>

      {cart && cart?.products?.length > 0 && <FooterScreenCartSummary />}
    </>
  );
}

const styles = StyleSheet.create({
  tabs: {
    marginTop: 10,
    flexDirection: "row",
    width: "100%",
    maxWidth: 600,
    alignSelf: "center",
    height: sn(50, 50, 60, 60),
    backgroundColor: tinycolor(colors.white).darken(1).toString(),
  },

  tab: { flex: 1, alignItems: "center", justifyContent: "center" },
});
